import { createPortal } from 'react-dom';
import { BaseSyntheticEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { classNames } from '@/shared/lib/classNames';
import { useResize } from '@/shared/lib/hooks/useResize/useResize';
import QuestionMark from '@/shared/assets/icons/questionMark.svg?react';
import QuestionMarkDark from '@/shared/assets/icons/questionMarkDark.svg?react';
import cls from './Tooltip.module.scss';

type Placement = 'bottom' | 'bottom-end' | 'bottom-start' | 'left' | 'right' | 'top' | 'top-end' | 'top-start';

type Props = {
    className?: string;
    iconDark?: boolean;
    maxWidth?: string;
    placement?: Placement;
    text: ReactNode | string;
    onClick?: (e: BaseSyntheticEvent) => void;
};

const Tooltip = (props: Props) => {
    const { placement = 'right', className, text, maxWidth = '400px', onClick, iconDark = false } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const width = useResize();
    const tooltipWidth = width < 768 ? '100vw' : maxWidth;

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;

        let top = 0;
        let left = 0;

        switch (placement) {
            case 'bottom':
                top = triggerRect.bottom + scrollY + 8;
                left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
                break;
            case 'bottom-end':
                top = triggerRect.bottom + scrollY + 8;
                left = triggerRect.right + scrollX - tooltipRect.width;
                break;
            case 'bottom-start':
                top = triggerRect.bottom + scrollY + 8;
                left = triggerRect.left + scrollX;
                break;
            case 'left':
                top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
                left = triggerRect.left + scrollX - tooltipRect.width - 8;
                break;
            case 'right':
                top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
                left = triggerRect.right + scrollX + 8;
                break;
            case 'top':
                top = triggerRect.top + scrollY - tooltipRect.height - 8;
                left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
                break;
            case 'top-end':
                top = triggerRect.top + scrollY - tooltipRect.height - 8;
                left = triggerRect.right + scrollX - tooltipRect.width;
                break;
            case 'top-start':
                top = triggerRect.top + scrollY - tooltipRect.height - 8;
                left = triggerRect.left + scrollX;
                break;
        }

        // Проверка границ экрана
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (left < 8) left = 8;
        if (left + tooltipRect.width > viewportWidth - 8) {
            left = viewportWidth - tooltipRect.width - 8;
        }
        if (top < scrollY + 8) top = scrollY + 8;
        if (top + tooltipRect.height > scrollY + viewportHeight - 8) {
            top = scrollY + viewportHeight - tooltipRect.height - 8;
        }

        setPosition({ top, left });
    }, [placement]);

    useEffect(() => {
        if (isOpen && triggerRef.current && tooltipRef.current) {
            // Небольшая задержка для правильного расчета размеров
            const timer = setTimeout(() => {
                calculatePosition();
            }, 0);

            const handleResize = () => {
                if (triggerRef.current && tooltipRef.current) {
                    calculatePosition();
                }
            };
            const handleScroll = () => {
                if (triggerRef.current && tooltipRef.current) {
                    calculatePosition();
                }
            };

            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);

            return () => {
                clearTimeout(timer);
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isOpen, calculatePosition]);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 100);
    };

    const handleTooltipMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const handleTooltipMouseLeave = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            <div
                className={classNames(cls.tooltip, {}, [className])}
                ref={triggerRef}
                onClick={onClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {iconDark ? <QuestionMarkDark /> : <QuestionMark />}
            </div>
            {isOpen &&
                createPortal(
                    <div
                        className={cls.tooltipContent}
                        style={{
                            top: `${position.top}px`,
                            left: `${position.left}px`,
                            maxWidth: tooltipWidth,
                        }}
                        ref={tooltipRef}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                    >
                        {text}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default Tooltip;
